import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { Link, useTranslation } from '@shared/core';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useExitWithoutCreatingDraftHandler } from '../useExitWithoutCreatingDraftHandler';
import { ChevronBack } from '@withjoy/joykit/icons';
import { Skeleton } from '@shared/components/Skeleton';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';

const Slash = () => <Box as="span">/</Box>;

const DesktopContainer: React.FC = ({ children }) => {
  return (
    <Flex
      typographyVariant="hed2"
      __css={{
        [' > *']: {
          paddingX: 4,
          paddingY: 1,
        },
      }}
    >
      {children}
    </Flex>
  );
};

export const Breadcrumbs = ({ designName, templateCategory }: { designName: string; templateCategory: StationeryTemplateCategoryEnum }) => {
  const { tq } = useTranslation('stationery');
  const isMobile = useIsMobileScreen();
  const { getShopPath } = useCardsRouterContext();

  const onClickRoot = useExitWithoutCreatingDraftHandler('breadcrumbsRootLink');
  const onClickCategory = useExitWithoutCreatingDraftHandler('breadcrumbsCategoryLink');

  const getCategoryName = useStationeryTemplateCategoryTranslations('designsGalleryPdpBreadcrumbCategory');

  // Standalone envelopes have no category gallery to link back to — the carrier template is
  // excluded from it by design — and `envelope` has no entry in the breadcrumb translation map
  // (externally-maintained locale file), so it would otherwise degrade to the generic default.
  // The crumb is therefore a plain label, and the design crumb is dropped: the page is the
  // product, so "Cards / Envelopes / Envelope" would just say it twice.
  const isEnvelope = templateCategory === StationeryTemplateCategoryEnum.envelope;
  const categoryName = isEnvelope ? tq`Envelopes` : getCategoryName(templateCategory);
  const categoryPath = isEnvelope ? getShopPath('wedding') : getShopPath('wedding', templateCategory);

  return isMobile ? (
    <Flex
      as={Link}
      display="inline-flex"
      alignItems="center"
      to={categoryPath}
      onClick={onClickCategory}
      columnGap={3}
    >
      <ChevronBack size={16} />
      <TextV2 typographyVariant="hed2">{categoryName}</TextV2>
    </Flex>
  ) : (
    <DesktopContainer>
      <Box
        as={Link}
        to={getShopPath('wedding')}
        onClick={onClickRoot}
      >
        {tq`Cards`}
      </Box>
      <Slash />
      {isEnvelope ? (
        <Box
          as="span"
          color="mono10"
          cursor="default"
        >
          {categoryName}
        </Box>
      ) : (
        <>
          <Box
            as={Link}
            to={categoryPath}
            onClick={onClickCategory}
          >
            {categoryName}
          </Box>
          <Slash />
          <Box
            as="span"
            color="mono10"
            cursor="default"
          >
            {designName}
          </Box>
        </>
      )}
    </DesktopContainer>
  );
};

const BreeadcrumbsSkeleton = () => {
  const isMobile = useIsMobileScreen();

  return isMobile ? (
    <Flex
      display="inline-flex"
      alignItems="center"
      columnGap={3}
    >
      <Skeleton />
      <Skeleton />
    </Flex>
  ) : (
    <DesktopContainer>
      <Skeleton width={60} />
      <Slash />
      <Skeleton width={120} />
      <Slash />
      <Skeleton width={200} />
    </DesktopContainer>
  );
};

Breadcrumbs.Skeleton = BreeadcrumbsSkeleton;
